import { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { setLookup } from "../state";
import lookupApi from "../apis/lookupApi";
import { LOOKUP_CATEGORIES, LOOKUP_CFR_TYPES } from "../utilities/constants";

/**
 * Custom hook to fetch and manage lookup data
 * Fetches lookup data if it doesn't exist in Redux store
 * @returns {Object} { lookup, isLoading }
 */
const useLookupData = () => {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  
  const lookup = useSelector((state) => state.lookup);
  const tenantId = useSelector((state) => state.tenantId);
  const idToken = useSelector((state) => state.token);

  useEffect(() => {
    // Skip if lookup data already exists or missing required auth
    if (lookup || !tenantId || !idToken) {
      return;
    }

    const fetchLookupData = async () => {
      try {
        setIsLoading(true);
        console.log("Loading global lookup data...");
        
        const requestHeader = {
          headers: {
            "x-eqip-tenantid": tenantId,
            Authorization: idToken,
          },
        };

        const [
          responseGeneral, 
          responsePaiDrug, 
          responsePaiApi, 
          // CFR211
          responseSection, 
          responseSubsection, 
          responseRegulation, 
          responseCitationLanguage,
          // Q7
          responseSectionQ7, 
          responseSubsectionQ7, 
          responseRegulationQ7, 
          responseCitationLanguageQ7,
          // CFR111
          responseSectionCfr111, 
          responseSubsectionCfr111, 
          responseRegulationCfr111, 
          responseCitationLanguageCfr111,
          // CFR820
          responseSectionCfr820, 
          responseSubsectionCfr820, 
          responseRegulationCfr820, 
          responseCitationLanguageCfr820,
          // CFR820211
          responseSectionCfr820211, 
          responseSubsectionCfr820211, 
          responseRegulationCfr820211, 
          responseCitationLanguageCfr820211,
          // CFR211820
          responseSectionCfr211820, 
          responseSubsectionCfr211820, 
          responseRegulationCfr211820, 
          responseCitationLanguageCfr211820,
          // General BIMO
          responseGeneralBimo
        ] = await Promise.all([
          lookupApi.get(`/lookups/categories/${LOOKUP_CATEGORIES.GENERAL}`, requestHeader),
          lookupApi.get(`/lookups/categories/${LOOKUP_CATEGORIES.PAI_DRUG}`, requestHeader),
          lookupApi.get(`/lookups/categories/${LOOKUP_CATEGORIES.PAI_API}`, requestHeader),
          // CFR211
          lookupApi.get(`/lookups/lookuptype/${LOOKUP_CFR_TYPES.SECTION}/lookupcodetypes/CFR211`, requestHeader),
          lookupApi.get(`/lookups/lookuptype/${LOOKUP_CFR_TYPES.SUB_SECTION}/lookupcodetypes/CFR211`, requestHeader),
          lookupApi.get(`/lookups/lookuptype/${LOOKUP_CFR_TYPES.REGULATION}/lookupcodetypes/CFR211`, requestHeader),
          lookupApi.get(`/lookups/lookuptype/${LOOKUP_CFR_TYPES.SHORT_CITATION_LANGUAGE}/lookupcodetypes/CFR211`, requestHeader),
          // Q7
          lookupApi.get(`/lookups/lookuptype/${LOOKUP_CFR_TYPES.SECTION}/lookupcodetypes/Q7API`, requestHeader),
          lookupApi.get(`/lookups/lookuptype/${LOOKUP_CFR_TYPES.SUB_SECTION}/lookupcodetypes/Q7API`, requestHeader),
          lookupApi.get(`/lookups/lookuptype/${LOOKUP_CFR_TYPES.REGULATION}/lookupcodetypes/Q7API`, requestHeader),
          lookupApi.get(`/lookups/lookuptype/${LOOKUP_CFR_TYPES.SHORT_CITATION_LANGUAGE}/lookupcodetypes/Q7API`, requestHeader),
          // CFR111
          lookupApi.get(`/lookups/lookuptype/${LOOKUP_CFR_TYPES.SECTION}/lookupcodetypes/CFR111`, requestHeader),
          lookupApi.get(`/lookups/lookuptype/${LOOKUP_CFR_TYPES.SUB_SECTION}/lookupcodetypes/CFR111`, requestHeader),
          lookupApi.get(`/lookups/lookuptype/${LOOKUP_CFR_TYPES.REGULATION}/lookupcodetypes/CFR111`, requestHeader),
          lookupApi.get(`/lookups/lookuptype/${LOOKUP_CFR_TYPES.SHORT_CITATION_LANGUAGE}/lookupcodetypes/CFR111`, requestHeader),
          // CFR820
          lookupApi.get(`/lookups/lookuptype/${LOOKUP_CFR_TYPES.SECTION}/lookupcodetypes/CFR820`, requestHeader),
          lookupApi.get(`/lookups/lookuptype/${LOOKUP_CFR_TYPES.SUB_SECTION}/lookupcodetypes/CFR820`, requestHeader),
          lookupApi.get(`/lookups/lookuptype/${LOOKUP_CFR_TYPES.REGULATION}/lookupcodetypes/CFR820`, requestHeader),
          lookupApi.get(`/lookups/lookuptype/${LOOKUP_CFR_TYPES.SHORT_CITATION_LANGUAGE}/lookupcodetypes/CFR820`, requestHeader),
          // CFR820211
          lookupApi.get(`/lookups/lookuptype/${LOOKUP_CFR_TYPES.SECTION}/lookupcodetypes/CFR820211`, requestHeader),
          lookupApi.get(`/lookups/lookuptype/${LOOKUP_CFR_TYPES.SUB_SECTION}/lookupcodetypes/CFR820211`, requestHeader),
          lookupApi.get(`/lookups/lookuptype/${LOOKUP_CFR_TYPES.REGULATION}/lookupcodetypes/CFR820211`, requestHeader),
          lookupApi.get(`/lookups/lookuptype/${LOOKUP_CFR_TYPES.SHORT_CITATION_LANGUAGE}/lookupcodetypes/CFR820211`, requestHeader),
          // CFR211820
          lookupApi.get(`/lookups/lookuptype/${LOOKUP_CFR_TYPES.SECTION}/lookupcodetypes/CFR211820`, requestHeader),
          lookupApi.get(`/lookups/lookuptype/${LOOKUP_CFR_TYPES.SUB_SECTION}/lookupcodetypes/CFR211820`, requestHeader),
          lookupApi.get(`/lookups/lookuptype/${LOOKUP_CFR_TYPES.REGULATION}/lookupcodetypes/CFR211820`, requestHeader),
          lookupApi.get(`/lookups/lookuptype/${LOOKUP_CFR_TYPES.SHORT_CITATION_LANGUAGE}/lookupcodetypes/CFR211820`, requestHeader),
          // General BIMO
          lookupApi.get(`/lookups/categories/${LOOKUP_CATEGORIES.GENERAL_BIMO}`, requestHeader),
        ]);

        dispatch(setLookup({ 
          lookup: {
            generals: responseGeneral.data.body,
            paiDrugs: responsePaiDrug.data.body,
            paiApis: responsePaiApi.data.body,
            // CFR211
            sections: responseSection.data.body,
            subSections: responseSubsection.data.body,
            regulations: responseRegulation.data.body,
            citationLanguages: responseCitationLanguage.data.body,
            // Q7
            sectionsQ7: responseSectionQ7.data.body,
            subSectionsQ7: responseSubsectionQ7.data.body,
            regulationsQ7: responseRegulationQ7.data.body,
            citationLanguagesQ7: responseCitationLanguageQ7.data.body,
            // CFR111
            sectionsCfr111: responseSectionCfr111.data.body,
            subSectionsCfr111: responseSubsectionCfr111.data.body,
            regulationsCfr111: responseRegulationCfr111.data.body,
            citationLanguagesCfr111: responseCitationLanguageCfr111.data.body,
            // CFR820
            sectionsCfr820: responseSectionCfr820.data.body,
            subSectionsCfr820: responseSubsectionCfr820.data.body,
            regulationsCfr820: responseRegulationCfr820.data.body,
            citationLanguagesCfr820: responseCitationLanguageCfr820.data.body,
            // CFR820211
            sectionsCfr820211: responseSectionCfr820211.data.body,
            subSectionsCfr820211: responseSubsectionCfr820211.data.body,
            regulationsCfr820211: responseRegulationCfr820211.data.body,
            citationLanguagesCfr820211: responseCitationLanguageCfr820211.data.body,
            // CFR211820
            sectionsCfr211820: responseSectionCfr211820.data.body,
            subSectionsCfr211820: responseSubsectionCfr211820.data.body,
            regulationsCfr211820: responseRegulationCfr211820.data.body,
            citationLanguagesCfr211820: responseCitationLanguageCfr211820.data.body,
            // General BIMO
            generalsBimo: responseGeneralBimo.data.body,
          }
        }));

        console.log("Global lookup data loaded successfully");
        setIsLoading(false);
      } catch (error) {
        console.error("Failed to load lookup data:", error);
        setIsLoading(false);
        throw error;
      }
    };

    fetchLookupData();
  }, [lookup, tenantId, idToken, dispatch]);

  return { lookup, isLoading };
};

export default useLookupData;

